import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Autocomplete, TextField, CircularProgress, Chip } from '@mui/material';
import { getCarrierDataFromMNR } from '../../Redux/MNR/ReportsTabSlice';
import { setSelectedRows } from '../../Redux/MNR/ReportsTabSecondTableSlice';

/**
 * CarrierMultiSelect
 * Replaces the carrier table with a searchable multi-select dropdown.
 * Uses MUI Autocomplete (multiple) for selection and search filtering.
 * Selected carriers are stored via existing setSelectedRows action (per tab) so
 * downstream Data Bricks job logic remains unchanged.
 */
const CarrierMultiSelect = ({ tabIndex, carriers, loading, error, selected, onSelectionChange, annualMode }) => {
    const dispatch = useDispatch();

    // Always call hooks first (no conditional hook usage), then override with controlled props if provided
    const reduxCarrierList = useSelector(state => state.reportsTab.tabs[tabIndex]?.carrierList || []);
    const reduxCarrierLoading = useSelector(state => state.reportsTab.tabs[tabIndex]?.carrierLoading || false);
    const reduxCarrierError = useSelector(state => state.reportsTab.tabs[tabIndex]?.carrierError || null);
    const reduxSelectedRows = useSelector(state => state.reportsTabSecondTable.tabs[tabIndex]?.selectedRows || []);

    const carrierList = carriers !== undefined ? carriers : reduxCarrierList;
    const carrierLoading = loading !== undefined ? loading : reduxCarrierLoading;
    const carrierError = error !== undefined ? error : reduxCarrierError;
    const selectedRows = selected !== undefined ? selected : reduxSelectedRows;

    // Fetch carriers on mount if not already loaded
    useEffect(() => {
        // Only auto-fetch in legacy (uncontrolled) mode
        if (carriers !== undefined) return;
        if (!carrierLoading && carrierList.length === 0 && !carrierError) {
            dispatch(getCarrierDataFromMNR({ tabIndex }));
        }
    }, [dispatch, tabIndex, carrierList.length, carrierLoading, carrierError, carriers]);

    const options = carrierList;

    const getOptionLabel = (option) => {
        if (!option) return '';
        const code = option.carrier || option.carrierCode || '';
        const desc = option.carrierDescription || '';
        const group = option.cocaGroupDescription || option.cocaGroup || '';
        return [code, desc, group ? `(${group})` : ''].filter(Boolean).join(' - ');
    };

    // Memoize selected values to objects in options (match by carrier field)
    const value = useMemo(() => {
        if (!Array.isArray(selectedRows)) return [];
        const byCarrier = Object.fromEntries(options.map(o => [o.carrier, o]));
        return selectedRows.map(r => byCarrier[r.carrier] || r).filter(Boolean);
    }, [selectedRows, options]);

    const handleChange = (_evt, newValue) => {
        if (onSelectionChange) {
            onSelectionChange(newValue);
        } else {
            dispatch(setSelectedRows({ tabIndex, selectedRows: newValue }));
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 600 }}>
            <Autocomplete
                multiple
                disableCloseOnSelect
                options={options}
                getOptionLabel={getOptionLabel}
                value={value}
                onChange={handleChange}
                loading={carrierLoading}
                filterSelectedOptions
                size="small"
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={option.carrier + index}
                            label={option.carrier}
                            size="small"
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={carrierError ? 'Carriers (failed to load)' : annualMode ? 'Select Annual Carriers' : 'Select Carriers'}
                        placeholder={carrierLoading ? 'Loading carriers...' : 'Type to search carriers'}
                        error={!!carrierError}
                        helperText={carrierError ? 'Unable to load carriers. Retry from dropdown.' : ''}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {carrierLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                noOptionsText={carrierLoading ? 'Loading...' : 'No carriers'}
            />
        </Box>
    );
};

export default CarrierMultiSelect;
